import React, { useCallback, useState } from 'react'
import { css } from '@emotion/react'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'
import Tooltip from '@groovehq/internal-design-system/lib/components/Tooltip/Tooltip'
import Attachment from '@groovehq/internal-design-system/lib/assets/icons/Attachment'
import Dropdown from '@groovehq/internal-design-system/lib/components/Dropdown/Dropdown'
import { useDropboxLinkAction } from './DropboxLinkButton'

const buttonStyle = css`
  overflow: visible;
`

const countStyle = theme => css`
  opacity: 1;
  transition: opacity 0.2s ease-in-out;
  position: absolute;
  font-size: ${theme.fontSize.tiny};
  top: -2px;
  right: -6px;
  text-align: center;
  display: block;
  width: 15px;
  z-index: -2;
  button:hover & {
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
  }
`

const AttachmentButton = React.memo(
  ({
    onClick,
    triggerId = 'attachmentUploadTrigger',
    attachments = [],
    children,
    getEditor,
    contextKey,
    draftId,
    onChange,
    ...rest
  }) => {
    const hasAttachments = attachments.length > 0
    const [visible, setVisible] = useState(false)
    const dropbox = useDropboxLinkAction({
      getEditor,
      contextKey,
      draftId,
      onChange,
    })

    const openAttachmentDialog = useCallback(
      () => {
        if (onClick) onClick()
        document.getElementById(triggerId).click()
      },
      [onClick, triggerId]
    )

    const handleSelect = source => {
      setVisible(false)
      if (source === 'computer') openAttachmentDialog()
      if (source === 'dropbox' && dropbox.ready) dropbox.onClick()
    }
    const captureSelection = () => {
      if (!visible) dropbox.captureSelection()
    }
    const handleKeyDown = event => {
      if (event.key === 'Tab') setVisible(false)
      if (
        !visible &&
        ['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)
      ) {
        captureSelection()
        if (dropbox.configured && event.key !== 'Enter') {
          event.preventDefault()
          event.stopPropagation()
          setVisible(true)
        }
      }
    }
    const button = (
      <Button
        type="icon"
        size="small"
        aria-label="Add an attachment"
        aria-haspopup={dropbox.configured ? 'menu' : undefined}
        aria-expanded={dropbox.configured ? visible : undefined}
        onClick={dropbox.configured ? undefined : openAttachmentDialog}
        css={buttonStyle}
        {...rest}
      >
        <Attachment />
        {hasAttachments && <span css={countStyle}>{attachments.length}</span>}
      </Button>
    )

    return (
      <React.Fragment>
        <div
          onMouseDownCapture={captureSelection}
          onKeyDownCapture={handleKeyDown}
        >
          <Tooltip title="Add an attachment" position="top" disabled={visible}>
            {dropbox.configured ? (
              <Dropdown
                visible={visible}
                onVisibleChange={setVisible}
                onSelect={handleSelect}
                position="top-start"
                strategy="fixed"
                portal="#overlays"
                autoHeight
                offset={4}
                overlay={
                  <Dropdown.Menu role="menu" aria-label="Attachment sources">
                    <Dropdown.Menu.Item itemKey="computer" role="menuitem">
                      Attach from computer
                    </Dropdown.Menu.Item>
                    <Dropdown.Menu.Item
                      itemKey="dropbox"
                      role="menuitem"
                      disabled={!dropbox.ready}
                      aria-disabled={!dropbox.ready}
                      data-test-id="insert-dropbox-links"
                    >
                      {dropbox.ready && 'Insert link from Dropbox'}
                      {!dropbox.ready && !dropbox.failed && 'Loading Dropbox…'}
                      {dropbox.failed && 'Dropbox unavailable'}
                    </Dropdown.Menu.Item>
                  </Dropdown.Menu>
                }
              >
                {button}
              </Dropdown>
            ) : (
              button
            )}
          </Tooltip>
        </div>
        {children}
      </React.Fragment>
    )
  }
)

export default AttachmentButton
